import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {WeakMap<HTMLDetailsElement, number>} */
  #motionIds = new WeakMap();

  /** @type {HTMLElement | null} */
  #header = null;

  /**
   * Starts a new open or close for one details element.
   * Older frames and finish callbacks for that element become stale.
   * @param {HTMLDetailsElement} details
   */
  #beginMotion(details) {
    const id = (this.#motionIds.get(details) ?? 0) + 1;
    this.#motionIds.set(details, id);
    return id;
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {number} id
   */
  #isCurrentMotion(details, id) {
    return this.#motionIds.get(details) === id;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    this.#header = this.closest('header-component');
    this.#header?.addEventListener('click', this.#onHeaderControlClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#header?.removeEventListener('click', this.#onHeaderControlClick);
    this.#header = null;
  }

  /**
   * Search, cart, and account live outside the menu. A click on them should
   * dismiss the drawer the same way the overlay does, without blocking the control.
   * @param {Event} event
   */
  #onHeaderControlClick = (event) => {
    if (!this.isOpen) return;

    const node = event.composedPath?.()[0];
    if (!(node instanceof Node) || this.refs.details.contains(node)) return;

    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Whether the main menu drawer is showing. The `open` attribute and the
   * `menu-open` class can diverge when the browser toggles `<details>` itself,
   * so the visible class is the source of truth.
   * @returns {boolean}
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * The click listener retargets `event.target` to the element that owns
   * `on:click`. `composedPath()` is still the real press, so a tap on a nested
   * accordion is not treated as a tap on the drawer summary.
   * @param {Event | undefined} event
   * @param {Element | null} summary
   */
  #clickIsOnSummary(event, summary) {
    if (!summary || !event) return false;
    const node = event.composedPath?.()[0];
    return node instanceof Node && summary.contains(node);
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   * @param {Event} [event]
   */
  toggle(event) {
    const summary = this.refs.details.querySelector(':scope > summary');
    if (this.#clickIsOnSummary(event, summary)) event?.preventDefault();

    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (this.#clickIsOnSummary(event, summary)) event?.preventDefault();

    summary.setAttribute('aria-expanded', 'true');
    details.open = true;

    if (target === 'submenu') this.#openAccordions(details);

    const motionId = this.#beginMotion(details);
    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (!this.#isCurrentMotion(details, motionId) || !details.open) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (!this.#isCurrentMotion(details, motionId)) return;
        trapFocus(details);
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const motionId = this.#beginMotion(details);
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (!this.#isCurrentMotion(details, motionId) || details.classList.contains('menu-open')) return;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Every group in a level-2 drawer starts expanded. Closing the main menu
   * clears every open details element, so this runs each time the submenu opens.
   * @param {HTMLDetailsElement} details
   */
  #openAccordions(details) {
    for (const accordion of details.querySelectorAll('.skrey-drawer__accordion')) {
      if (accordion instanceof HTMLDetailsElement) accordion.open = true;
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
